import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Plus, Power, Store, ClipboardList, Star, TrendingUp, Copy, LogOut, Trash2, CreditCard, Pencil, LayoutTemplate } from "lucide-react";
import api, { money, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { toast } from "sonner";
import AdminHomeBuilder from "@/pages/admin/AdminHomeBuilder";

export default function SuperAdmin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("branches");
  const [branches, setBranches] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [txns, setTxns] = useState([]);
  const [stats, setStats] = useState(null);
  const [loyalty, setLoyalty] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", owner_email: "" });
  const [invite, setInvite] = useState(null);
  const [pwForm, setPwForm] = useState({ email: "", new_password: "" });
  const [pwBusy, setPwBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "super_admin") { navigate("/login"); return; }
    load();
  }, [user, loading]);

  const load = () => {
    api.get("/super/branches").then(({ data }) => setBranches(data)).catch(() => {});
    api.get("/super/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/super/loyalty").then(({ data }) => setLoyalty(data)).catch(() => {});
  };
  const loadOrders = () => api.get("/super/orders").then(({ data }) => setOrders(data)).catch(() => {});
  const loadReviews = () => api.get("/super/reviews").then(({ data }) => setReviews(data)).catch(() => {});
  const loadTxns = () => api.get("/payments/super/transactions").then(({ data }) => setTxns(data)).catch(() => {});

  useEffect(() => { if (tab === "orders") loadOrders(); if (tab === "reviews") loadReviews(); if (tab === "transactions") loadTxns(); }, [tab]);

  const toggleStatus = async (b) => {
    const next = b.status === "active" ? "inactive" : "active";
    try {
      await api.patch(`/super/branches/${b.id}/status`, { status: next });
      toast.success(`Branch ${next}`);
      load();
    } catch { toast.error("Could not update branch status"); }
  };

  const createBranch = async () => {
    if (!form.name || !form.owner_email) { toast.error("Enter name and owner email"); return; }
    const { data } = await api.post("/super/branches", form);
    setInvite(data.invite_link);
    setForm({ name: "", owner_email: "" });
    toast.success("Branch created & invite sent");
    load();
  };

  const resetPassword = async () => {
    if (!pwForm.email || !pwForm.new_password) { toast.error("Enter email and new password"); return; }
    if (pwForm.new_password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setPwBusy(true);
    try {
      await api.post("/super/users/reset-password", { email: pwForm.email.trim(), new_password: pwForm.new_password });
      toast.success(`Password updated for ${pwForm.email.trim()}`);
      setPwForm({ email: "", new_password: "" });
    } catch (e) { toast.error(apiError(e)); }
    finally { setPwBusy(false); }
  };

  const deleteBranch = async (b) => {
    if (!window.confirm(`Delete "${b.name}"? This permanently removes the branch, its menu and its staff accounts. Branches with orders can't be deleted — deactivate them instead.`)) return;
    try {
      await api.delete(`/super/branches/${b.id}`);
      toast.success("Branch deleted");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const renameBranch = async (b) => {
    const name = window.prompt("New branch name", b.name);
    if (name == null) return;
    const trimmed = name.trim();
    if (!trimmed || trimmed === b.name) return;
    try {
      await api.patch(`/super/branches/${b.id}`, { name: trimmed });
      toast.success("Branch renamed");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (loading || !user) return null;

  const TABS = [["branches", Building2], ["orders", ClipboardList], ["transactions", CreditCard], ["reviews", Star], ["overview", TrendingUp], ["homepage", LayoutTemplate]];

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <header className="bg-metros-black text-white px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 size={22} className="text-metros-red" />
          <h1 className="font-heading font-extrabold text-lg">METROS Super Admin</h1>
        </div>
        <button data-testid="super-logout" onClick={() => { logout(); navigate("/login"); }} className="flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-white"><LogOut size={16} /> Sign Out</button>
      </header>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-6">
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {TABS.map(([t, Ic]) => (
            <button key={t} data-testid={`super-tab-${t}`} onClick={() => setTab(t)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold capitalize whitespace-nowrap ${tab === t ? "bg-metros-red text-white" : "bg-white border border-gray-200"}`}>
              <Ic size={15} /> {t}
            </button>
          ))}
        </div>

        {tab === "branches" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading font-bold text-2xl">Branches ({branches.length})</h2>
              <button data-testid="super-add-branch" onClick={() => { setInvite(null); setCreating(true); }} className="bg-metros-red text-white rounded-full px-5 py-2.5 text-sm font-bold flex items-center gap-1"><Plus size={16} /> Invite Branch</button>
            </div>
            <div className="grid gap-3">
              {branches.map((b) => (
                <div key={b.id} data-testid={`super-branch-${b.id}`} className="bg-white rounded-2xl p-4 flex items-center gap-4">
                  <div className="bg-metros-red/10 text-metros-red rounded-xl p-3"><Store size={22} /></div>
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-bold">{b.name}</p>
                    <p className="text-xs text-muted-foreground">{b.owner_email || "—"} · {b.order_count} orders · {b.review_count} reviews {b.registered ? "" : "· ⏳ pending registration"}</p>
                  </div>
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${b.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"}`}>{b.status}</span>
                  <label className="flex items-center gap-2 text-xs font-semibold"><Power size={14} /><Switch data-testid={`super-toggle-${b.id}`} checked={b.status === "active"} onCheckedChange={() => toggleStatus(b)} /></label>
                  <button data-testid={`super-rename-${b.id}`} onClick={() => renameBranch(b)} title="Rename branch"
                    className="text-gray-400 hover:text-metros-black transition-colors p-1"><Pencil size={16} /></button>
                  {b.id !== "branch-1" && (
                    <button data-testid={`super-delete-${b.id}`} onClick={() => deleteBranch(b)} title="Delete branch"
                      className="text-gray-400 hover:text-metros-red transition-colors p-1"><Trash2 size={16} /></button>
                  )}
                </div>
              ))}
            </div>

            <div className="bg-white rounded-2xl p-5 mt-6 max-w-md" data-testid="reset-password-card">
              <h3 className="font-heading font-bold mb-1">Reset an account password</h3>
              <p className="text-xs text-muted-foreground mb-3">Set a new password for any admin, branch owner, staff or customer account.</p>
              <div className="space-y-3">
                <input data-testid="reset-email-input" placeholder="Account email" value={pwForm.email} onChange={(e) => setPwForm({ ...pwForm, email: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm" />
                <input data-testid="reset-password-input" type="password" placeholder="New password (min 8 chars)" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm" />
                <button data-testid="reset-password-submit" onClick={resetPassword} disabled={pwBusy} className="w-full bg-metros-black hover:bg-black text-white rounded-full py-3 font-bold disabled:opacity-50 transition-colors">{pwBusy ? "Updating…" : "Update Password"}</button>
              </div>
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div>
            <h2 className="font-heading font-bold text-2xl mb-4">All Orders ({orders.length})</h2>
            <div className="grid gap-2">
              {orders.map((o) => (
                <div key={o.id} className="bg-white rounded-xl p-3 flex items-center justify-between text-sm" data-testid={`super-order-${o.order_number}`}>
                  <div><span className="font-bold">{o.order_number}</span> <span className="text-muted-foreground">· {(branches.find((b) => b.id === o.branch_id) || {}).name || o.branch_id}</span></div>
                  <div className="flex items-center gap-3"><span className="text-muted-foreground">{o.type}</span><span className="font-bold">{money(o.total)}</span><span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full">{o.status}</span></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "transactions" && (
          <div data-testid="super-transactions">
            <h2 className="font-heading font-bold text-2xl mb-4">Card Transactions ({txns.length})</h2>
            <div className="grid gap-2">
              {txns.map((t) => (
                <div key={t.session_id} className="bg-white rounded-xl p-3 flex items-center justify-between text-sm" data-testid={`super-txn-${t.session_id}`}>
                  <div><span className="font-bold">{t.order_number || "—"}</span> <span className="text-muted-foreground">· {t.branch_name || "—"}</span></div>
                  <div className="flex items-center gap-3">
                    <span className="font-bold">{money(t.amount || 0)}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${t.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>{t.payment_status}</span>
                  </div>
                </div>
              ))}
              {txns.length === 0 && <p className="text-sm text-muted-foreground">No transactions yet.</p>}
            </div>
          </div>
        )}

        {tab === "reviews" && (
          <div>
            <h2 className="font-heading font-bold text-2xl mb-4">All Reviews ({reviews.length})</h2>
            <div className="grid gap-2">
              {reviews.map((r) => (
                <div key={r.id} className="bg-white rounded-xl p-3 text-sm" data-testid={`super-review-${r.id}`}>
                  <div className="flex justify-between"><span className="font-bold">{r.name}</span><span className="text-metros-red">{"★".repeat(r.rating)}</span></div>
                  <p className="text-muted-foreground">{r.comment}</p>
                  <p className="text-[11px] text-gray-400 mt-1">{(branches.find((b) => b.id === r.branch_id) || {}).name || r.branch_id}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "overview" && stats && (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="bg-white rounded-2xl p-5"><p className="text-xs text-muted-foreground">Branches</p><p className="font-heading font-extrabold text-3xl">{stats.branch_count}</p></div>
              <div className="bg-white rounded-2xl p-5"><p className="text-xs text-muted-foreground">Total Revenue</p><p className="font-heading font-extrabold text-3xl">{money(stats.total_revenue)}</p></div>
              <div className="bg-white rounded-2xl p-5"><p className="text-xs text-muted-foreground">Loyalty Points Live</p><p className="font-heading font-extrabold text-3xl">{loyalty?.total_points ?? 0}</p></div>
              <div className="bg-white rounded-2xl p-5"><p className="text-xs text-muted-foreground">Members</p><p className="font-heading font-extrabold text-3xl">{loyalty?.customer_count ?? 0}</p></div>
            </div>

            <div className="bg-white rounded-2xl p-5 mb-6" data-testid="revenue-chart">
              <h3 className="font-heading font-bold text-lg mb-4">Revenue by Branch</h3>
              <div className="space-y-3">
                {stats.branches.map((b) => {
                  const max = Math.max(...stats.branches.map((x) => x.revenue), 1);
                  return (
                    <div key={b.id}>
                      <div className="flex justify-between text-sm mb-1"><span className="font-semibold">{b.name}</span><span>{money(b.revenue)} · {b.orders} orders</span></div>
                      <div className="h-3 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-metros-red rounded-full transition-all" style={{ width: `${Math.max((b.revenue / max) * 100, 2)}%` }} /></div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white rounded-2xl p-5" data-testid="loyalty-overview">
              <h3 className="font-heading font-bold text-lg mb-4">Top Loyalty Members</h3>
              {loyalty?.top?.length ? (
                <div className="grid gap-2">
                  {loyalty.top.map((c, i) => (
                    <div key={i} className="flex justify-between text-sm border-b border-gray-50 pb-2">
                      <span>{c.name} <span className="text-muted-foreground text-xs">{c.email}</span></span>
                      <span className="font-bold text-metros-red">{c.loyalty_points} pts</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">No loyalty activity yet.</p>}
            </div>
          </div>
        )}

        {tab === "homepage" && (
          <div data-testid="super-homepage-builder">
            <AdminHomeBuilder />
          </div>
        )}
      </div>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-md w-[92vw] rounded-3xl p-6 bg-white border-0" data-testid="create-branch-modal">
          <VisuallyHidden><DialogTitle>Invite a branch</DialogTitle><DialogDescription>Create a branch and email an invite to the owner.</DialogDescription></VisuallyHidden>
          <h2 className="font-heading font-extrabold text-xl mb-3">Invite a Branch</h2>
          {!invite ? (
            <div className="space-y-3">
              <input data-testid="branch-name-input" placeholder="Branch name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm" />
              <input data-testid="branch-email-input" placeholder="Owner email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm" />
              <button data-testid="branch-create-submit" onClick={createBranch} className="w-full bg-metros-red text-white rounded-full py-3 font-bold">Create & Send Invite</button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-green-700 bg-green-50 rounded-xl p-3">Branch created. Invite link (also emailed to the owner):</p>
              <div className="flex gap-2">
                <input readOnly value={invite} className="flex-1 rounded-xl border border-gray-200 px-3 py-2.5 text-xs" data-testid="invite-link" />
                <button onClick={() => { navigator.clipboard.writeText(invite); toast.success("Copied"); }} className="bg-metros-black text-white rounded-xl px-3"><Copy size={16} /></button>
              </div>
              <div className="border border-gray-100 rounded-xl p-3 bg-gray-50 text-sm space-y-1.5" data-testid="new-branch-checklist">
                <p className="font-heading font-bold text-metros-black">Get this branch live — quick checklist</p>
                <p className="text-green-700">✓ Meal deals — shared across all branches, already set up (managed by you in Settings).</p>
                <p className="text-gray-700">☐ Menu &amp; pictures — add items, or copy from another branch in <b>Menu › Copy Menu</b> (includes images).</p>
                <p className="text-gray-700">☐ Opening hours &amp; delivery/collection — set in the branch's <b>Settings</b>.</p>
                <p className="text-gray-700">☐ Logo &amp; contact email — set the branch logo and email in <b>Settings</b>.</p>
              </div>
              <button onClick={() => setCreating(false)} className="w-full border border-gray-200 rounded-full py-2.5 font-semibold text-sm">Done</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
